import { renderToStaticMarkup } from 'react-dom/server';
import {
  claimNoOf,
  isDebitCard,
  type ClaimNoteHeader,
  type ClaimNoteItem,
  type ClaimNoteScrapSummary,
} from '@/utils/approval/claimNote';

/** 작성·조회 화면이 이 클래스로 업체파일·Print·삭제 버튼을 그림 */
export const CARD_CLASS = 'cn-card';
export const ACTION_CELL_CLASS = 'cn-action';

const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

/** PHP number_format — 값이 없으면 0 */
const numberFormat = (v: unknown, decimals = 0) => {
  const n = Number(text(v).replace(/,/g, '')) || 0;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const has = (v: unknown) => text(v) !== '';

const ActionCell = ({
  item,
  colSpan,
  attachPath,
}: {
  item: ClaimNoteItem;
  colSpan: number;
  attachPath?: string;
}) => {
  const oz = item.card.ozPrint ?? {};
  return (
    <th
      className={`text-right bg-info ${ACTION_CELL_CLASS}`}
      colSpan={colSpan}
      data-attach-path={attachPath || undefined}
      data-oz-url={text(oz.url)}
      data-oz-ozr={text(oz.gerpOzrNm)}
      data-oz-odi={text(oz.gerpOdiNm)}
      data-oz-pcount={text(oz.pcount)}
      data-oz-args1={text(oz.args1)}
      data-oz-locale={text(oz.locale)}
      data-oz-kind={text(oz.claimKind ?? item.card.claimKind)}
    />
  );
};

/** 레거시 appr_sub_view_forms_craim_debit_mstr */
const DebitTable = ({ item }: { item: ClaimNoteItem }) => {
  const headers = item.card.headers ?? [];
  const first: ClaimNoteHeader = headers[0] ?? {};
  return (
    <table className="table table-bordered">
      <colgroup>
        {[10, 7, 10, 8, 10, 13, 8, 8, 9, 8, 9].map((w, i) => (
          <col key={i} width={`${w}%`} />
        ))}
      </colgroup>
      <tbody>
        <tr>
          <th className="text-center bg-info" colSpan={7}>
            {`${claimNoOf(item.card, item.label)}-(${text(first.craimType)})`}
          </th>
          <ActionCell
            item={item}
            colSpan={4}
            attachPath={text(first.attachPath)}
          />
        </tr>
        <tr>
          <th colSpan={11} className="text-center bg-success">
            Summary
          </th>
        </tr>
        <tr className="bg-title">
          <td colSpan={3}>VENDOR</td>
          <td rowSpan={2}>BRAND</td>
          <td rowSpan={2}>CUSTOMER</td>
          <td rowSpan={2}>CREDIT NOTE#</td>
          <td colSpan={4} className="bg-warning">
            TOTAL
          </td>
          <td rowSpan={2}>CURRENCY</td>
        </tr>
        <tr className="bg-title">
          <td>NAME</td>
          <td>CODE</td>
          <td>
            CLAIM <br />
            DATE
          </td>
          <td>STYLE</td>
          <td>STYLE-COLOR</td>
          <td>CLAIM QUANTITY</td>
          <td>CLAIM AMOUNT</td>
        </tr>
        {headers.map((h, i) => (
          <tr key={i} className="text-center">
            <td>{text(h.custCdNm)}</td>
            <td>{text(h.bpCd)}</td>
            <td>{text(h.bpSendDateCh)}</td>
            <td>{text(h.brandNm)}</td>
            <td>{text(h.customer)}</td>
            <td>{text(h.creditNoBiz)}</td>
            <td>{text(h.style)}</td>
            <td>{text(h.colorCd)}</td>
            <td>{numberFormat(h.qty)}</td>
            <td>{numberFormat(h.sumKrw, 2)}</td>
            <td>{text(h.currCd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * 레거시 SCRAP 카드 합계 — 값이 없으면 수량·금액 0, 환율 빈칸.
 * 칸 배치(AMOUNT 칸에 수량 등)도 레거시 그대로
 */
const scrapValues = (raw: ClaimNoteItem['card']['scrapSummary']) => {
  const s: ClaimNoteScrapSummary = Array.isArray(raw)
    ? Object.assign({}, ...raw)
    : raw ?? {};
  return {
    scrapQty: has(s.scrapClaimQty) ? numberFormat(s.scrapClaimQty, 2) : '0',
    scrapAmount: has(s.scrapClaimAmt) ? numberFormat(s.scrapClaimAmt) : '0',
    exchRate: has(s.claimExchRate) ? numberFormat(s.claimExchRate) : '',
    debitQty: has(s.debitQty) ? numberFormat(s.debitQty, 2) : '0',
    debitAmount: has(s.exchClaimAmt) ? numberFormat(s.exchClaimAmt, 2) : '0',
  };
};

/** 레거시 appr_sub_view_forms_craim_scrap_mstr */
const ScrapTable = ({ item }: { item: ClaimNoteItem }) => {
  const headers = item.card.headers ?? [];
  const first: ClaimNoteHeader = headers[0] ?? {};
  const v = scrapValues(item.card.scrapSummary);
  return (
    <table className="table table-bordered">
      <colgroup>
        {Array.from({ length: 8 }, (_, i) => (
          <col key={i} width="12.5%" />
        ))}
      </colgroup>
      <tbody>
        <tr>
          <th className="text-center bg-info" colSpan={6}>
            {`SCRAP NOTE (${claimNoOf(item.card, item.label)})-(${text(
              first.craimType,
            )})`}
          </th>
          <ActionCell item={item} colSpan={2} />
        </tr>
        <tr>
          <th colSpan={8} className="text-center bg-success">
            Summary
          </th>
        </tr>
        <tr className="bg-title">
          <td rowSpan={2}>CUSTOMER</td>
          <td colSpan={4}>SCRAP</td>
          <td colSpan={3}>DEBIT</td>
        </tr>
        <tr className="bg-title">
          <td className="bg-warning">AMOUNT</td>
          <td className="bg-warning">CURRENCY</td>
          <td className="bg-warning">FX RATE</td>
          <td className="bg-warning">AMOUNT(KRW)</td>
          <td className="bg-warning">CURRENCY</td>
          <td className="bg-warning">AMOUNT</td>
          <td className="bg-warning">CURRENCY</td>
        </tr>
        {headers.map((h, i) => (
          <tr key={i} className="text-center">
            <td>{text(h.custCdNm)}</td>
            <td>{v.scrapQty}</td>
            <td>{text(h.currCd)}</td>
            <td>{v.exchRate}</td>
            <td>{v.scrapAmount}</td>
            <td>{v.debitQty}</td>
            <td>{v.debitAmount}</td>
            <td>{text(h.currCd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/** 결재 건마다 카드 1개 — 유형(DEBIT/SCRAP)에 따라 표가 다름 */
const Card = ({ item, gubun }: { item: ClaimNoteItem; gubun?: string }) => (
  <div
    className={CARD_CLASS}
    data-item-id={item.aprvId}
    data-label={item.label}
    data-aprv-rev={item.aprvRev}
  >
    {isDebitCard(item.card, gubun) ? (
      <DebitTable item={item} />
    ) : (
      <ScrapTable item={item} />
    )}
  </div>
);

/** 저장 본문 — 고른 순서대로 카드 */
export const buildClaimNoteHtml = (
  items: ClaimNoteItem[],
  gubun?: string,
): string =>
  items.length
    ? renderToStaticMarkup(
        <>
          {items.map((item) => (
            <Card key={item.aprvId} item={item} gubun={gubun} />
          ))}
        </>,
      )
    : '';
